import React, { useState, useEffect, useCallback, useRef } from 'react';
import { __, sprintf } from '@wordpress/i18n';
import { klona } from 'klona';
import { isEmpty, isNil } from 'ramda';

// api
import CompanyService from '../../../../service/company-service';

// tools
import { isEmail, isPIVA } from '../../../../helpers/validators';

// components
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { classNames } from 'primereact/utils';
import { Editor } from 'primereact/editor';
import ChipsStandalone from '../../../../components/ChipsStandalone';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Messages } from 'primereact/messages';
import { useDebounce } from 'primereact/hooks';
import set404FromErrorResponse from '../../../../helpers/set404FromErrorResponse';
import UserService from '../../../../service/user-service';

const DEFAULT_INVITE_MESSAGE = "<p>Gentile Fornitore,</p><p>l'Albo Fornitori di GruppoHTR racconta la rete di partner con cui l'azienda lavora fianco a fianco, con trasparenza e fiducia reciproca. I fornitori contribuiscono alle performance ESG dell'azienda, impegnandosi nella riduzione dell'impatto ambientale, nel rispetto dei diritti dei lavoratori e in una governance trasparente.</p><p>Vi invitiamo a entrare a far parte del nostro Albo Fornitori, procedendo con l'iscrizione al seguente link:</p><p><a href=\"https://portale-fornitore.gruppohtr.com/login\">https://portale-fornitore.gruppohtr.com/login</a></p><p>Cordiali saluti</p>";

const DialogInviteSupplier = () => {
    const [loading, setLoading] = useState(false);
    const [isVisibleInviteDialog, setIsVisibleInviteDialog] = useState(false);
    const [newUserData, setNewUserData] = useState({});
    const errorMsgs = useRef(null);
    const toast = useRef(null);
    const [, debouncedPivaValue, setInputPiva] = useDebounce('', 1000);
    const [isVatValid, setIsVatValid] = useState(true);

    const headerEditDialog = () => {
        return <span>{__('Invita Nuovo Fornitore', 'htr')}</span>
    }

    const hideEditDialog = () => {
        setIsVisibleInviteDialog(false);
        setIsVatValid(false);
        setNewUserData({});
    }

    const sendInvitations = useCallback(() => {
        if (!isInvalidAllFields(newUserData) && (!hasVatNumber(newUserData) || isVatValid)) {
            const formData = {
                "beneficiary": {
                    "firstName": newUserData.firstName,
                    "lastName": newUserData.lastName,
                    "email": newUserData.email,
                    "phoneNumber": newUserData.phoneNumber
                },
                "companyRequest": {
                    "name": newUserData.companyName,
                    "vatNumber": newUserData.vatNumber
                },
                "customMessage": newUserData.customMessage,
                "lookupDataIds": isNil(newUserData.categories) ? [] : [newUserData.categories]
            }

            UserService.inviteSupplier(formData, invitesCallback, errInvitesCallbacks)
        }
    }, [newUserData]);

    const invitesCallback = (resp) => {
        if (resp.status === 'SUCCESS') {
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: '',
                    detail: resp.message
                });
            }
        }
    }

    const errInvitesCallbacks = (resp) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: '',
                detail: resp.message
            });
        }
        setLoading(false);
    }

    const onChangeEditItem = (value, key) => {
        setNewUserData((prev) => {
            const userData = klona(prev);
            userData[key] = value;
            if (key === 'vatNumber') {
                setInputPiva(value);
            }
            return userData;
        });
    }

    const isInvalidField = (data, key) => key === 'vatNumber'
        ? !isPIVA(data[key])
        : isEmpty(data[key]) || isNil(data[key]);

    const isEmailInvalid = (data) => isEmpty(data.email) || isNil(data.email) || !isEmail(data.email);
    const isCompanyInvalid = (data) => isEmpty(data.companyName) || isNil(data.companyName);
    const hasVatNumber = (data) => !isEmpty(data.vatNumber) && !isNil(data.vatNumber);
    const isVatFieldInvalid = (data) => hasVatNumber(data) && !isPIVA(data.vatNumber);
    // Obbligatori: solo Email (valida) e Azienda. Il resto e' opzionale; la P.IVA e' validata solo se compilata.
    const isInvalidAllFields = (data) => isEmailInvalid(data) || isCompanyInvalid(data) || isVatFieldInvalid(data);

    const checkVatNumber = (value) => {
        if (errorMsgs.current) {
            errorMsgs.current.clear();
        }
        const isValid = isPIVA(value);

        if (isValid) {
            setLoading(true);
            CompanyService.checkVat(checkVatCallback, errCheckVatCallback, [
                ['vatNumber', value],
                ['alreadyExistsInDb', true]
            ])
        }
    }

    const checkVatCallback = (data) => {
        if (data.status === 'SUCCESS') {
            if (!data.data.valid) {
                setIsVatValid(false);
                errorMsgs.current.show([
                    {
                        sticky: true, severity: 'error', summary: '',
                        detail: sprintf(__('%s', 'htr'), data.data.message),
                        closable: true
                    }
                ]);
            } else {
                setIsVatValid(true);
            }

            const version = data.data.version;
            const resp = data.data?.vatCheckResponse?.data;
            if (resp && !isEmpty(resp)) {
                if (version === 'V2') {
                    const firstItem = resp[0];
                    const { companyName } = firstItem;
                    onChangeEditItem(companyName, 'companyName');
                }
            }
        }

        setLoading(false);
    }

    const errCheckVatCallback = (resp) => {
        set404FromErrorResponse(resp);
        setLoading(false);
    }

    const footerEditDialog = useCallback(() => {
        return <div>
            <Button type="button" label={__('Annulla', 'htr')} onClick={hideEditDialog} outlined/>
            <Button
                type="button"
                disabled={loading || isInvalidAllFields(newUserData) || (hasVatNumber(newUserData) && !isVatValid)}
                label={__('Invia', 'htr')} onClick={sendInvitations}/>
        </div>
    }, [newUserData]);

    const renderHeader = () => {
        return (
            <span className="ql-formats">
                <button className="ql-bold" aria-label="Bold"></button>
                <button className="ql-italic" aria-label="Italic"></button>
                <button className="ql-underline" aria-label="Underline"></button>
                <button className="ql-link" aria-label="Link"></button>
                <button className="ql-list" value="ordered"></button>
                <button className="ql-header" value="2"></button>
                <button className="ql-header" value="3"></button>
                <button className="ql-blockquote"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-indent" value="-1"></button>
                <button className="ql-indent" value="+1"></button>
            </span>
        );
    };

    const header = renderHeader();

    useEffect(() => {
        checkVatNumber(debouncedPivaValue);
    }, [debouncedPivaValue])

    useEffect(() => {
        if (!isVisibleInviteDialog) {
            setIsVatValid(false);
            setNewUserData({});
        } else {
            setNewUserData((prev) => (prev && prev.customMessage)
                ? prev
                : { ...prev, customMessage: DEFAULT_INVITE_MESSAGE });
        }
    }, [isVisibleInviteDialog]);

    return (
        <>
            <Toast ref={toast}/>
            <Button
                type="button"
                onClick={() => setIsVisibleInviteDialog(true)}
                label={__('Aggiungi Fornitore', 'htr')}
                icon="pi pi-plus" iconPos="right"/>

            {isVisibleInviteDialog
                ? <Dialog
                    visible={isVisibleInviteDialog}
                    modal
                    header={headerEditDialog}
                    footer={footerEditDialog}
                    style={{ maxWidth: '600px', width: '100%' }}
                    onHide={hideEditDialog}>
                    <div className="appForm__cols">
                        <div className="appForm__field">
                            <label>
                                {__('Nome', 'htr')}
                            </label>
                            <InputText value={newUserData.firstName || ''}
                                       onChange={(e) => onChangeEditItem(e.target.value, 'firstName')}/>
                        </div>
                        <div className="appForm__field">
                            <label>
                                {__('Cognome', 'htr')}
                            </label>
                            <InputText value={newUserData.lastName || ''}
                                       onChange={(e) => onChangeEditItem(e.target.value, 'lastName')}/>
                        </div>
                    </div>
                    <div className="appForm__cols">
                        <div className="appForm__field">
                            <label
                                className={classNames({ 'p-error': isEmpty(newUserData.email) || isNil(newUserData.email) || !isEmail(newUserData.email) })}>
                                {__('Email', 'htr')}*
                            </label>
                            <InputText value={newUserData.email || ''}
                                       invalid={isEmpty(newUserData.email) || isNil(newUserData.email) || !isEmail(newUserData.email)}
                                       onChange={(e) => onChangeEditItem(e.target.value, 'email')}/>
                        </div>
                        <div className="appForm__field">
                            <label>
                                {__('Telefono', 'htr')}
                            </label>
                            <InputText value={newUserData.phoneNumber || ''}
                                       keyfilter="int"
                                       onChange={(e) => onChangeEditItem(e.target.value, 'phoneNumber')}/>
                        </div>
                    </div>
                    <div className="appForm__cols">
                        <div className="appForm__field">
                            <label
                                className={classNames({ 'p-error': isInvalidField(newUserData, 'companyName') })}>
                                {__('Azienda', 'htr')}*
                            </label>
                            <InputText value={newUserData.companyName || ''}
                                       disabled={loading}
                                       invalid={isInvalidField(newUserData, 'companyName')}
                                       onChange={(e) => onChangeEditItem(e.target.value, 'companyName')}/>
                        </div>
                        <div className="appForm__field">
                            <label
                                className={classNames({ 'p-error': isVatFieldInvalid(newUserData) })}>
                                {__('Partita IVA', 'htr')}
                            </label>
                            <InputText value={newUserData.vatNumber || ''}
                                       disabled={loading}
                                       invalid={isVatFieldInvalid(newUserData)}
                                       onChange={(e) => onChangeEditItem(e.target.value, 'vatNumber')}/>
                        </div>
                    </div>
                    <Messages ref={errorMsgs}/>
                    <div className="appForm__field">
                        <ChipsStandalone
                            value={newUserData.categories || ''}
                            multiple={false}
                            label={__('Categorie', 'htr')}
                            onChange={(ids) => onChangeEditItem(ids, 'categories')}
                            disabled={loading}
                        />
                    </div>
                    <div className="appForm__field">
                        <label>
                            {__('Messaggio (opzionale)', 'htr')}
                        </label>
                        <div translate="no">
                            <Editor
                                value={newUserData.customMessage || ''}
                                readOnly={false}
                                placeholder={__('Digita qui il messagio', 'htr')}
                                headerTemplate={header}
                                onTextChange={(e) => onChangeEditItem(e.htmlValue, 'customMessage')}
                                style={{ height: 80 * 3, width: '100%' }}
                            />
                        </div>
                    </div>
                </Dialog> : null}
        </>
    )
}

export default DialogInviteSupplier;
